import { supabase } from './supabaseClient.js';

const SESSION_ID_KEY = 'soapShowdown.sessionId';
const SUBMITTED_FLAG_PREFIX = 'soapShowdown.submitted.';

/** crypto.randomUUID() only exists in secure contexts (HTTPS/localhost) and
 * on modern browsers. Fall back to crypto.getRandomValues, and finally to
 * Math.random, so a stray HTTP load or an old WebView never breaks the
 * "Start Showdown" button outright — this id is just an idempotency key,
 * not a security token, so a non-cryptographic fallback is fine. */
function makeSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
}

/** One session id per browser tab lifetime (sessionStorage), used as the
 * idempotency key for the completion RPC. A fresh session id is minted the
 * next time the visitor starts a new showdown. */
export function getOrCreateSessionId() {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = makeSessionId();
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

export function startNewSession() {
  const id = makeSessionId();
  sessionStorage.setItem(SESSION_ID_KEY, id);
  return id;
}

function submittedFlagKey(sessionId) {
  return `${SUBMITTED_FLAG_PREFIX}${sessionId}`;
}

export function wasAlreadySubmitted(sessionId) {
  return sessionStorage.getItem(submittedFlagKey(sessionId)) === '1';
}

/**
 * Commits a completed showdown via the atomic `submit_showdown` RPC.
 * Safe to call more than once for the same sessionId: the RPC is idempotent
 * server-side (unique session id), and this also short-circuits client-side
 * so a result-screen refresh never even issues the network call twice.
 *
 * @param {string} sessionId
 * @param {string} winnerSlug
 * @param {Array<{round: number, winnerSlug: string, loserSlug: string}>} rounds
 */
export async function submitShowdown(sessionId, winnerSlug, rounds) {
  if (wasAlreadySubmitted(sessionId)) {
    return { alreadySubmitted: true };
  }

  const { error } = await supabase.rpc('submit_showdown', {
    p_session_id: sessionId,
    p_winner_slug: winnerSlug,
    p_rounds: rounds.map((r) => ({
      round_number: r.round,
      winner_slug: r.winnerSlug,
      loser_slug: r.loserSlug,
    })),
  });

  if (error) throw error;

  sessionStorage.setItem(submittedFlagKey(sessionId), '1');
  return { alreadySubmitted: false };
}

/** Returns leaderboard rows already sorted per the ranking rules, plus the
 * total number of completed sessions used for the percentage denominator. */
export async function fetchLeaderboard() {
  const { data, error } = await supabase.rpc('get_leaderboard');
  if (error) throw error;

  const rows = (data ?? []).map((row) => ({
    id: row.ingredient_id,
    slug: row.slug,
    name: row.name,
    poster: `/posters/${row.slug}.jpg`,
    finalVotes: Number(row.final_votes),
    roundWins: Number(row.round_wins),
    percentage: Number(row.percentage),
  }));

  const completedSessions = rows.reduce((sum, r) => sum + r.finalVotes, 0);
  return { rows, completedSessions };
}
